import { treeTypes } from './types';
import {
	getDescendants,
	getLastAccessible,
	getNextAccessible,
	getParent,
	getPreviousAccessible,
	isBranchNode,
	getAccessibleRange,
	propagatedIds,
} from './utils';

export const handleKeyDown = ({
	data,
	expandedIds,
	selectedIds,
	disabledIds,
	tabbableId,
	dispatch,
	propagateCollapse,
	propagateSelect,
	multiSelect,
	expandOnKeyboardSelect,
	togglableSelect,
}: any) => (event: any) => {
	const element = data[tabbableId];
	const id = element.id;
	if (event.ctrlKey) {
		if (event.key === 'a') {
			event.preventDefault();
			const { 0: root, ...dataWithoutRoot } = data;
			const ids = Object.values(dataWithoutRoot)
				.map((x: any) => x.id)
				.filter(id => !disabledIds.has(id));
			dispatch({
				type: treeTypes.changeSelectMany,
				multiSelect,
				select: Array.from(selectedIds).filter(id => !disabledIds.has(id)).length !== ids.length,
				ids,
				lastInteractedWith: element.id,
			});
		} else if (event.shiftKey && (event.key === 'Home' || event.key === 'End')) {
			const newId = event.key === 'Home' ? data[0].children[0] : getLastAccessible(data, id, expandedIds);
			const range = getAccessibleRange({
				data,
				expandedIds,
				from: id,
				to: newId,
			}).filter((id: any) => !disabledIds.has(id));
			dispatch({
				type: treeTypes.changeSelectMany,
				multiSelect,
				select: true,
				ids: propagateSelect ? propagatedIds(data, range, disabledIds) : range,
			});
			dispatch({
				type: treeTypes.focus,
				id: newId,
				lastInteractedWith: newId,
			});
		}
		return;
	}

	if (event.shiftKey) {
		switch (event.key) {
			case 'ArrowUp': {
				event.preventDefault();
				const previous = getPreviousAccessible(data, id, expandedIds);
				if (previous != null || !disabledIds.has(previous)) {
					dispatch({
						type: treeTypes.changeSelectMany,
						ids: propagateSelect ? propagatedIds(data, [previous], disabledIds) : [previous],
						select: true,
						multiSelect,
						lastInteractedWith: previous,
					});
					dispatch({
						type: treeTypes.focus,
						id: previous,
						lastInteractedWith: previous,
					});
				}
				return;
			}
			case 'ArrowDown': {
				event.preventDefault();
				const next = getNextAccessible(data, id, expandedIds);
				if (next != null || !disabledIds.has(next)) {
					dispatch({
						type: treeTypes.changeSelectMany,
						ids: propagateSelect ? propagatedIds(data, [next], disabledIds) : [next],
						multiSelect,
						select: true,
						lastInteractedWith: next,
					});
					dispatch({
						type: treeTypes.focus,
						id: next,
						lastInteractedWith: next,
					});
				}
				return;
			}
			default:
				break;
		}
	}
	switch (event.key) {
		case 'ArrowDown': {
			event.preventDefault();
			const next = getNextAccessible(data, id, expandedIds);
			if (next != null) {
				dispatch({
					type: treeTypes.focus,
					id: next,
					lastInteractedWith: next,
				});
			}
			return;
		}
		case 'ArrowUp': {
			event.preventDefault();
			const previous = getPreviousAccessible(data, id, expandedIds);
			if (previous != null) {
				dispatch({
					type: treeTypes.focus,
					id: previous,
					lastInteractedWith: previous,
				});
			}
			return;
		}
		case 'ArrowLeft': {
			event.preventDefault();
			if (isBranchNode(data, id) && expandedIds.has(tabbableId)) {
				if (propagateCollapse) {
					const ids = [id, ...getDescendants(data, id, new Set())];
					dispatch({
						type: treeTypes.collapseMany,
						ids,
						lastInteractedWith: element.id,
					});
				} else {
					dispatch({
						type: treeTypes.collapse,
						id,
						lastInteractedWith: id,
					});
				}
			} else {
				const isRoot = data[0].children.includes(id);
				if (!isRoot) {
					const parentId = getParent(data, id);
					dispatch({
						type: treeTypes.focus,
						id: parentId,
						lastInteractedWith: parentId,
					});
				}
			}
			return;
		}
		case 'ArrowRight': {
			event.preventDefault();
			if (isBranchNode(data, id)) {
				if (expandedIds.has(tabbableId)) {
					dispatch({
						type: treeTypes.focus,
						id: element.children[0],
						lastInteractedWith: element.children[0],
					});
				} else {
					dispatch({ type: treeTypes.expand, id, lastInteractedWith: id });
				}
			}
			return;
		}
		case 'Home':
			event.preventDefault();
			dispatch({
				type: treeTypes.focus,
				id: data[0].children[0],
				lastInteractedWith: data[0].children[0],
			});
			break;
		case 'End': {
			event.preventDefault();
			const lastAccessible = getLastAccessible(data, data[0].id, expandedIds);
			dispatch({
				type: treeTypes.focus,
				id: lastAccessible,
				lastInteractedWith: lastAccessible,
			});
			return;
		}
		case '*': {
			event.preventDefault();
			const nodes = data[getParent(data, id)].children.filter((x: any) => isBranchNode(data, x));
			dispatch({
				type: treeTypes.expandMany,
				ids: nodes,
				lastInteractedWith: id,
			});
			return;
		}
		//IE11 uses "Spacebar"
		case 'Enter':
		case ' ':
		case 'Spacebar':
			event.preventDefault();
			dispatch({
				type: togglableSelect ? treeTypes.toggleSelect : treeTypes.select,
				id: id,
				multiSelect,
				lastInteractedWith: id,
			});
			propagateSelect &&
				!disabledIds.has(element.id) &&
				dispatch({
					type: treeTypes.changeSelectMany,
					ids: propagatedIds(data, [id], disabledIds),
					select: togglableSelect ? !selectedIds.has(id) : true,
					multiSelect,
					lastInteractedWith: id,
				});
			expandOnKeyboardSelect && dispatch({ type: treeTypes.toggle, id, lastInteractedWith: id });
			return;
		default:
			if (event.key.length === 1) {
				let currentId = getNextAccessible(data, id, expandedIds);
				while (currentId !== id) {
					if (currentId == null) {
						currentId = data[0].children[0];
						continue;
					}
					if (data[currentId].name[0].toLowerCase() === event.key.toLowerCase()) {
						dispatch({
							type: treeTypes.focus,
							id: currentId,
							lastInteractedWith: id,
						});
						return;
					}
					currentId = getNextAccessible(data, currentId, expandedIds);
				}
			}
			return;
	}
};
